"use client";

import { c as _c } from "react/compiler-runtime";
import type { InitialConfigType } from "@lexical/react/LexicalComposer";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { forwardRef, useImperativeHandle, useCallback, useEffect, useRef } from "react";
import type { EditorState } from "lexical";
import { RootNode, COMMAND_PRIORITY_CRITICAL, createCommand } from "lexical";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { ListNode, ListItemNode } from "@lexical/list";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import type { MentionedUser, ReferencedNft } from "@/entities/IDrop";
import { ActiveDropAction } from "@/types/dropInteractionTypes";
import { MentionNode } from "../drops/create/lexical/nodes/MentionNode";
import { HashtagNode } from "../drops/create/lexical/nodes/HashtagNode";
import { ImageNode } from "../drops/create/lexical/nodes/ImageNode";
import ExampleTheme from "../drops/create/lexical/ExampleTheme";
import { assertUnreachable } from "@/helpers/AllowlistToolHelpers";
import type { ClearEditorPluginHandles } from "../drops/create/lexical/plugins/ClearEditorPlugin";
import ClearEditorPlugin from "../drops/create/lexical/plugins/ClearEditorPlugin";
import type { NewMentionsPluginHandles } from "../drops/create/lexical/plugins/mentions/MentionsPlugin";
import NewMentionsPlugin from "../drops/create/lexical/plugins/mentions/MentionsPlugin";
import type { NewHastagsPluginHandles } from "../drops/create/lexical/plugins/hashtags/HashtagsPlugin";
import NewHashtagsPlugin from "../drops/create/lexical/plugins/hashtags/HashtagsPlugin";
import { MaxLengthPlugin } from "../drops/create/lexical/plugins/MaxLengthPlugin";
import DragDropPastePlugin from "../drops/create/lexical/plugins/DragDropPastePlugin";
import EnterKeyPlugin from "../drops/create/lexical/plugins/enter/EnterKeyPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import CreateDropEmojiPicker from "./CreateDropEmojiPicker";
import useCapacitor from "@/hooks/useCapacitor";
import EmojiPlugin from "../drops/create/lexical/plugins/emoji/EmojiPlugin";
import { EmojiNode } from "../drops/create/lexical/nodes/EmojiNode";
import { SAFE_MARKDOWN_TRANSFORMERS } from "@/components/drops/create/lexical/transformers/markdownTransformers";
import PlainTextPastePlugin from "@/components/drops/create/lexical/plugins/PlainTextPastePlugin";
export interface CreateDropInputHandles {
  clearEditorState: () => void;
  focus: () => void;
}

// Create a custom command
const DISABLE_EDIT_COMMAND = createCommand("DISABLE_EDIT");

// Create a custom plugin to handle disabling
function DisableEditPlugin(t0) {
  const $ = _c(4);
  const {
    disabled
  } = t0;
  const [editor] = useLexicalComposerContext();
  let t1;
  let t2;
  if ($[0] !== disabled || $[1] !== editor) {
    t1 = () => {
      if (disabled) {
        return editor.registerCommand(DISABLE_EDIT_COMMAND, _temp, COMMAND_PRIORITY_CRITICAL);
      }
      return _temp2;
    };
    t2 = [editor, disabled];
    $[0] = disabled;
    $[1] = editor;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
}
function _temp2() {}
function _temp() {
  return true;
}
const CreateDropInput = forwardRef<CreateDropInputHandles, {
  readonly waveId: string;
  readonly editorState: EditorState | null;
  readonly type: ActiveDropAction | null;
  readonly canSubmit: boolean;
  readonly isStormMode: boolean;
  readonly submitting: boolean;
  readonly isDropMode: boolean;
  readonly onDrop?: (() => void) | undefined;
  readonly onEditorState: (editorState: EditorState) => void;
  readonly onReferencedNft: (referencedNft: ReferencedNft) => void;
  readonly onMentionedUser: (mentionedUser: Omit<MentionedUser, "current_handle">) => void;
}>((t0, ref) => {
  const $ = _c(71);
  const {
    waveId,
    editorState,
    type,
    canSubmit,
    isStormMode,
    isDropMode,
    submitting,
    onEditorState,
    onReferencedNft,
    onMentionedUser,
    onDrop
  } = t0;
  const {
    isCapacitor
  } = useCapacitor();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [MentionNode, HashtagNode, RootNode, HeadingNode, ListNode, ListItemNode, QuoteNode, CodeNode, CodeHighlightNode, TableNode, TableCellNode, TableRowNode, AutoLinkNode, LinkNode, HorizontalRuleNode, ImageNode, EmojiNode];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = !submitting;
  let t3;
  if ($[1] !== editorState || $[2] !== t2) {
    t3 = {
      namespace: "User Drop",
      nodes: t1,
      editorState,
      editable: t2,
      onError(error) {
        throw error;
      },
      theme: ExampleTheme
    };
    $[1] = editorState;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const editorConfig = t3;
  let t4;
  if ($[4] !== onEditorState) {
    t4 = editorState_0 => onEditorState(editorState_0);
    $[4] = onEditorState;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const onEditorStateChange = t4;
  let t5;
  if ($[6] !== onMentionedUser) {
    t5 = user => {
      onMentionedUser(user);
    };
    $[6] = onMentionedUser;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const onMentionedUserAdded = t5;
  let t6;
  if ($[8] !== onReferencedNft) {
    t6 = hashtag => onReferencedNft(hashtag);
    $[8] = onReferencedNft;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const onHashtagAdded = t6;
  let t7;
  if ($[10] !== isDropMode || $[11] !== isStormMode || $[12] !== type) {
    t7 = () => {
      if (isStormMode) {
        return "Add to the storm";
      }
      if (type === null) {
        return isDropMode ? "Create a drop" : "Create a post";
      }
      switch (type) {
        case ActiveDropAction.REPLY:
          {
            return isDropMode ? "Drop a reply" : "Post a reply";
          }
        case ActiveDropAction.QUOTE:
          {
            return isDropMode ? "Quote a drop" : "Post a quote";
          }
        default:
          {
            assertUnreachable(type);
            return "";
          }
      }
    };
    $[10] = isDropMode;
    $[11] = isStormMode;
    $[12] = type;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const getPlaceHolderText = t7;
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    const urlRegExp = new RegExp(/((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[\w]*))?)/);
    t8 = function validateUrl(url) {
      return url === "https://" || urlRegExp.test(url);
    };
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const validateUrl = t8;
  const clearEditorRef = useRef(null);
  const editorRef = useRef(null);
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      clearEditorRef.current?.clearEditorState();
    };
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const clearEditorState = t9;
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => ({
      clearEditorState,
      focus: () => {
        (editorRef.current?.querySelector("[contenteditable=\"true\"]") as HTMLElement).focus();
      }
    });
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  useImperativeHandle(ref, t10);
  const mentionsPluginRef = useRef(null);
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => !!mentionsPluginRef.current?.isMentionsOpen();
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const isMentionsOpen = t11;
  const hashtagPluginRef = useRef(null);
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    const isHashtagsOpen = () => !!hashtagPluginRef.current?.isHashtagsOpen();
    t12 = () => !isMentionsOpen() && !isHashtagsOpen();
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const canSubmitWithEnter = t12;
  const canSubmitRef = useRef(canSubmit);
  const onDropRef = useRef(onDrop);
  let t13;
  let t14;
  if ($[19] !== canSubmit) {
    t13 = () => {
      canSubmitRef.current = canSubmit;
    };
    t14 = [canSubmit];
    $[19] = canSubmit;
    $[20] = t13;
    $[21] = t14;
  } else {
    t13 = $[20];
    t14 = $[21];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[22] !== onDrop) {
    t15 = () => {
      onDropRef.current = onDrop;
    };
    t16 = [onDrop];
    $[22] = onDrop;
    $[23] = t15;
    $[24] = t16;
  } else {
    t15 = $[23];
    t16 = $[24];
  }
  useEffect(t15, t16);
  let t17;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => {
      if (!canSubmitRef.current || !onDropRef.current) {
        return;
      }
      onDropRef.current();
    };
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  const handleSubmit = t17;
  let t18;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      touchAction: "manipulation"
    };
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== submitting) {
    t19 = e => {
      const target = e.currentTarget;
      if (!submitting) {
        Promise.resolve().then(() => {
          target.focus();
          const selection = window.getSelection();
          if (selection?.rangeCount === 0) {
            const range = document.createRange();
            range.selectNodeContents(target);
            range.collapse(false);
            selection.addRange(range);
          }
        });
      }
    };
    $[27] = submitting;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  const t20 = `editor-input-one-liner tw-form-input tw-block tw-max-h-[40vh] tw-w-full tw-resize-none tw-rounded-lg tw-border-0 tw-bg-iron-900 tw-py-2.5 tw-pl-3 tw-text-base tw-font-normal tw-leading-6 tw-text-white tw-caret-primary-400 tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-iron-700 tw-transition tw-duration-300 tw-ease-out tw-scrollbar-thin tw-scrollbar-track-iron-900 tw-scrollbar-thumb-iron-600 placeholder:tw-text-iron-500 focus:tw-bg-iron-950 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400 sm:tw-text-sm ${submitting ? "tw-cursor-default tw-opacity-50" : ""} ${isCapacitor ? "tw-pr-[35px]" : "tw-pr-[40px]"}`;
  let t21;
  if ($[29] !== t19 || $[30] !== t20) {
    t21 = <ContentEditable spellCheck={true} autoCorrect="on" style={t18} onClick={t19} className={t20} />;
    $[29] = t19;
    $[30] = t20;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  let t22;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <CreateDropEmojiPicker />;
    $[32] = t22;
  } else {
    t22 = $[32];
  }
  let t23;
  if ($[33] !== t21) {
    t23 = <div className="tw-relative">{t21}{t22}</div>;
    $[33] = t21;
    $[34] = t23;
  } else {
    t23 = $[34];
  }
  const t24 = `editor-placeholder ${submitting ? "tw-opacity-50" : ""}`;
  const t25 = getPlaceHolderText();
  let t26;
  if ($[35] !== t24 || $[36] !== t25) {
    t26 = <span className={t24}>{t25}</span>;
    $[35] = t24;
    $[36] = t25;
    $[37] = t26;
  } else {
    t26 = $[37];
  }
  let t27;
  if ($[38] !== t23 || $[39] !== t26) {
    t27 = <RichTextPlugin contentEditable={t23} placeholder={t26} ErrorBoundary={LexicalErrorBoundary} />;
    $[38] = t23;
    $[39] = t26;
    $[40] = t27;
  } else {
    t27 = $[40];
  }
  let t28;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <HistoryPlugin />;
    $[41] = t28;
  } else {
    t28 = $[41];
  }
  let t29;
  if ($[42] !== onEditorStateChange) {
    t29 = <OnChangePlugin onChange={onEditorStateChange} />;
    $[42] = onEditorStateChange;
    $[43] = t29;
  } else {
    t29 = $[43];
  }
  let t30;
  if ($[44] !== onMentionedUserAdded || $[45] !== waveId) {
    t30 = <NewMentionsPlugin waveId={waveId} onSelect={onMentionedUserAdded} ref={mentionsPluginRef} />;
    $[44] = onMentionedUserAdded;
    $[45] = waveId;
    $[46] = t30;
  } else {
    t30 = $[46];
  }
  let t31;
  if ($[47] !== onHashtagAdded) {
    t31 = <NewHashtagsPlugin onSelect={onHashtagAdded} ref={hashtagPluginRef} />;
    $[47] = onHashtagAdded;
    $[48] = t31;
  } else {
    t31 = $[48];
  }
  let t32;
  let t33;
  let t34;
  let t35;
  let t36;
  let t37;
  let t38;
  let t39;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <MaxLengthPlugin maxLength={25000} />;
    t33 = <DragDropPastePlugin />;
    t34 = <ListPlugin />;
    t35 = <PlainTextPastePlugin />;
    t36 = <MarkdownShortcutPlugin transformers={SAFE_MARKDOWN_TRANSFORMERS} />;
    t37 = <TabIndentationPlugin />;
    t38 = <LinkPlugin validateUrl={validateUrl} />;
    t39 = <ClearEditorPlugin ref={clearEditorRef} />;
    $[49] = t32;
    $[50] = t33;
    $[51] = t34;
    $[52] = t35;
    $[53] = t36;
    $[54] = t37;
    $[55] = t38;
    $[56] = t39;
  } else {
    t32 = $[49];
    t33 = $[50];
    t34 = $[51];
    t35 = $[52];
    t36 = $[53];
    t37 = $[54];
    t38 = $[55];
    t39 = $[56];
  }
  let t40;
  let t41;
  if ($[57] !== submitting) {
    t40 = <DisableEditPlugin disabled={submitting} />;
    t41 = <EnterKeyPlugin handleSubmit={handleSubmit} canSubmitWithEnter={canSubmitWithEnter} disabled={submitting} />;
    $[57] = submitting;
    $[58] = t40;
    $[59] = t41;
  } else {
    t40 = $[58];
    t41 = $[59];
  }
  let t42;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <EmojiPlugin />;
    $[60] = t42;
  } else {
    t42 = $[60];
  }
  let t43;
  if ($[61] !== t27 || $[62] !== t29 || $[63] !== t30 || $[64] !== t31 || $[65] !== t40 || $[66] !== t41) {
    t43 = <div className="tw-flex tw-items-end tw-gap-x-3"><div className="tw-relative tw-w-full">{t27}{t28}{t29}{t30}{t31}{t32}{t33}{t34}{t35}{t36}{t37}{t38}{t39}{t40}{t41}{t42}</div></div>;
    $[61] = t27;
    $[62] = t29;
    $[63] = t30;
    $[64] = t31;
    $[65] = t40;
    $[66] = t41;
    $[67] = t43;
  } else {
    t43 = $[67];
  }
  let t44;
  if ($[68] !== editorConfig || $[69] !== t43) {
    t44 = <div className="tailwind-scope" ref={editorRef}><LexicalComposer initialConfig={editorConfig}>{t43}</LexicalComposer></div>;
    $[68] = editorConfig;
    $[69] = t43;
    $[70] = t44;
  } else {
    t44 = $[70];
  }
  return t44;
});
CreateDropInput.displayName = "CreateDropInput";
export default CreateDropInput;